import { Inject, Injectable } from '@angular/core';
import { FormControl } from '@angular/forms';
import { UtilsBsHotToastService } from '@brunsker/libs/core/utils/hot-toast/src';
import { ENVIRONMENT_TOKEN, Environment } from '@brunsker/libs/core/utils/http-log-interceptor/src';
import {
  DepartamentoFiltersService,
  DepartamentosFilters,
} from '@brunsker/libs/portal-do-vendedor/ui/departamento-filters';
import { Product } from '@brunsker/libs/portal-do-vendedor/ui/product-card';
import {
  MapperService,
  SelectClientService,
} from '@brunsker/libs/portal-do-vendedor/utils/portal-vendedor-api';
import { FormGroup } from '@ngneat/reactive-forms';
import {
  Observable,
  map,
  of,
  shareReplay
} from 'rxjs';
import { PaginatorResponse } from '../../../../models/login.models';
import {
  ProdutoSearchRequest,
  ProdutoSearchResponse,
} from '../../../../models/product.models';
import { AmountValueService } from '../../../api/amount-value/amount-value.service';
import { NewApiCatalogoService } from '../../../api/new-api-catalogo/new-api-catalogo.service';
import { NewApiProductService } from '../../../api/new-api-product/new-api-product.service';
import { VendedorSelectService } from '../../../api/vendedor-service/vendedor-service.service';
import {
  ORDER_FILTERS,
  PRICE_FILTERS,
} from '../helpers/product-predicate.filter';
import { STEPS } from './Steps.enum';

@Injectable({
  providedIn: 'root',
})
export class DepartamentosDomainService {
  products$: Observable<Product[]> = of([]);
  hasClient = this.selectClientService.hasClient$$;
  currentDepartamento = '';
  totalPage!: number;
  total!: number;
  passAllsteps: boolean = false;
  defaultFilial: number = 1;
  sessionStorage = window.sessionStorage;
  block: boolean = false;

  formGroup = new FormGroup({
    lancamento: new FormControl(false, { nonNullable: true }),
    acabaramDeChegar: new FormControl(false, { nonNullable: true }),
    promocao: new FormControl(false, { nonNullable: true }),
  });

  autorizaUso = new FormControl(true);
  currentStep: number = 0;
  hasObrigatory: boolean = this.environment.passSteps;

  constructor(
    readonly mapperService: MapperService,
    readonly newApiProductService: NewApiProductService,
    private readonly selectClientService: SelectClientService,
    private readonly toast: UtilsBsHotToastService,
    private readonly departamentoFiltersService: DepartamentoFiltersService,
    private readonly newApiCatalogoService: NewApiCatalogoService,
    private readonly amountValueService: AmountValueService,
    private readonly bsHotToastService: UtilsBsHotToastService,
    private readonly vendedorSelectService: VendedorSelectService,
    @Inject(ENVIRONMENT_TOKEN) readonly environment: Environment,
  ) {}

  getDepartamentoOptions() {
    return this.newApiCatalogoService.searchDepartment().pipe(
      map((catalogos) =>
        catalogos.flatMap((catalogo) => catalogo.departamentos ?? []),
      ),
    );
  }

  start(index: number, length: number): void {
    this.sessionStorage.getItem('passAllSteps')
    ? this.passAllsteps = true
    : this.passAllsteps = false;

    if (this.hasObrigatory && !this.passAllsteps) {
      this.formGroup.disable();
      this.checkOption();
    }

    this.verifyCheck(index, length);
  }

  verifyCheck(index: number = 0, length: number = 30)
  {
    this.searchProduct(index, length);
  }

  searchProduct(index?: number, length?: number, departamentosFilters?: DepartamentosFilters) {
    const filter: DepartamentosFilters = departamentosFilters ??
    {
      ...this.departamentoFiltersService.filters$$.value
    }

    const filterPas = this.setFilters(filter)

    const requestPrices = PRICE_FILTERS[filterPas.preco];
    const requestOrder = ORDER_FILTERS[filterPas.ordenar];
    const client = this.selectClientService.getCliente();
    const user = JSON.parse(sessionStorage.getItem('user') ?? '');

    const parameters = {
      pLANCAMENTO: this.formGroup.controls.lancamento.value ? 1 : 0,
      pNOVOEST: this.formGroup.controls.acabaramDeChegar.value ? 1 : 0,
      pPRECOPROM: this.formGroup.controls.promocao.value ? 1 : 0,
    };

    const request: ProdutoSearchRequest = {
      codigoUsuario: client?.vendedor ?? user.idVendedor,
      codigoPlanoPagamento: client?.planoDePagamento,
      codigoCobranca: client?.formaDePagamento,
      FILIAL: client?.filial ?? this.defaultFilial,
      INTERVALO: length ?? 30,
      LINHAINICIAL: index ?? 0,
      PESQUISA: filterPas.searchFormArray,
      CODDEPTO: +filterPas.departamento || 0,
      ...requestOrder,
      ...requestPrices,
      codigoCliente: client?.id,
      pLANCAMENTO: parameters.pLANCAMENTO,
      pNOVOEST: parameters.pNOVOEST,
      pPRECOPROM: parameters.pPRECOPROM,
      matricula: this.vendedorSelectService.vendedorActiv(),
      origemPedido: client?.origemVenda,
    };

    this.products$ = this.newApiProductService.getInformationProduct(request).pipe(
          map((resp) => {
            if (resp.total == 0) {
              this.bsHotToastService.showWarning(
                'Nenhum produto encontrado com base nesses filtros'
              );
            }
            return resp;
          }),
          this.mappedProducts(),
          shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  backStep() {
    if (this.currentStep > STEPS.LANCAMENTO) this.currentStep--;
    this.checkOption();
  }

  forwardStep() {
    if (this.currentStep < STEPS.PROMOCAO) this.currentStep++;
    this.checkOption();
  }

  checkOption() {
    if(this.currentStep === STEPS.PROMOCAO) {
      this.passAllsteps = true;
      this.sessionStorage.setItem('passAllSteps', 'true');
      this.autorizaUso.setValue(true);
    };

    const form = Object.keys(this.formGroup.controls);
    const currentItem = form[this.currentStep];

    form.forEach((key, i) => {
      const method = i <= this.currentStep || this.passAllsteps ? 'enable' : 'disable';
      const control =
        this.formGroup.controls[key as keyof typeof this.formGroup.controls];
      control.setValue(key == currentItem, { emitEvent: false });
      control[method]({ emitEvent: false });
    });
    this.searchProduct();
  }

  verifyClick() {
    if (this.autorizaUso.value) return;
    this.toast.showInfo(
      'Passe por todas as etapas para selecionar as opções bloqueadas.'
    );
  }

  mappedProducts() {
    return map((products: PaginatorResponse<ProdutoSearchResponse>) => {
      this.totalPage = products.totalPage;
      this.total = products.total;

      const productsMapped = products.data.map((product) => {
        const taxa = +product.preco * this.amountValueService.AMOUNT_VALUE;

        return {
          id: product.sku,
          name: product.nomE_PRODUTO,
          image: product.dirfotoprod,
          stock: +product.qT_DISPONIVEL,
          newPrice: +product.preco + taxa,
          olderPrice: +product.preco,
          embalagem: product.embalagem,
          filial: product.iD_FILIAL,
          multiplo: product.multiplo,
          descontoMaximo: product.descontoMaximoResponse,
          pLANCAMENTO: product.pLANCAMENTO,
          pNOVOEST: product.pNOVOEST,
          pPRECOPROM: product.pPRECOPROM,
          embalagensprod: product.embalagensprod,
          embalagens: product.embalagens,
          unidade: product.unidademaster,
        } as Product;
      });
      return productsMapped;
    });
  }

  setFilters(filters: DepartamentosFilters): {
    departamento: string;
    preco: string;
    ordenar: string;
    searchFormArray?: string[] | string;
  } {
    const removeLetters = filters.departamento
      ? filters.departamento.replace(/[^0-9]/g, '')
      : '';

    let res;
    if (filters.useState) {
      res = this.departamentoFiltersService.currentFilters;
    } else {
      res = {
        departamento: removeLetters,
        preco: filters.preco,
        ordenar: filters.ordenar,
        searchFormArray: filters.searchFormArray
      };
      this.currentDepartamento = filters.departamento;
    }
    this.departamentoFiltersService.currentFilters = res;
    this.departamentoFiltersService.currentDepartamento =
      this.currentDepartamento;
    return res;
  }
  listProducts() {
    this.block = false;
  }
  blockProducts() {
    this.block = true;
  }
  setCategory(categoryName: string) {
    sessionStorage.removeItem('productFilter');
    if (categoryName &&
      ((this.hasObrigatory && this.passAllsteps)||
      !this.hasObrigatory)) {
      let controls = this.formGroup.controls
      Object.keys(controls).forEach((key) => {
        this.formGroup.controls[key as keyof typeof controls].setValue(key == categoryName ?true :false, { emitEvent: false });
      });
      this.searchProduct();
    }
  }
}
